import { inject, Service } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { CanActivateFn, CanDeactivateFn } from '@angular/router';
import { AuthenticationService } from '@core/authentication';
import { environment } from '@environment';

export const joinContactImportHub: CanActivateFn = async () => {
    const contactImportHubService = inject(ContactImportHubService);

    try {
        await contactImportHubService.startConnection();
    } catch (error) {
        console.error('Failed to join ContactImport hub:', error);
    }

    return true;
};

export const leaveContactImportHub: CanDeactivateFn<unknown> = async () => {
    const contactImportHubService = inject(ContactImportHubService);

    try {
        await contactImportHubService.stopConnection();
    } catch (error) {
        console.error('Failed to leave ContactImport hub:', error);
    }

    return true;
};

@Service()
export class ContactImportHubService {
    private readonly authenticationService = inject(AuthenticationService);
    public readonly contactImportValidationFinished$ = new Subject<void>();
    public readonly contactImportProgressChanged$ = new Subject<void>();
    private readonly hubConnection: HubConnection;
    private connectionPromise: Promise<void> | null = null;

    constructor() {
        this.hubConnection = new HubConnectionBuilder()
            .withAutomaticReconnect()
            .withUrl(`${environment.apiConfig.api.baseUri}hubs/contact-import`, {
                accessTokenFactory: () => this.authenticationService.acquireAccessTokenForSignalRHub(),
                transport: HttpTransportType.ServerSentEvents,
            })
            .configureLogging(LogLevel.Warning)
            .build();

        this.hubConnection.on('ContactImportValidationFinished', () => {
            this.contactImportValidationFinished$.next();
        });

        this.hubConnection.on('ContactImportProgressChanged', () => {
            this.contactImportProgressChanged$.next();
        });
    }

    public async startConnection(): Promise<void> {
        if (this.hubConnection.state === HubConnectionState.Connected) {
            return;
        }

        if (this.hubConnection.state === HubConnectionState.Connecting) {
            if (this.connectionPromise) {
                return this.connectionPromise;
            }
        }

        if (this.hubConnection.state === HubConnectionState.Disconnected) {
            this.connectionPromise = this.hubConnection.start();
            try {
                await this.connectionPromise;
            } finally {
                this.connectionPromise = null;
            }
        }
    }

    public async stopConnection(): Promise<void> {
        if (this.hubConnection.state === HubConnectionState.Connected) {
            await this.hubConnection.stop();
        }
        this.connectionPromise = null;
    }

    public async joinContactImport(contactImportId: string): Promise<void> {
        await this.ensureConnected();
        return await this.hubConnection.invoke<void>('JoinContactImport', contactImportId);
    }

    public async leaveContactImport(contactImportId: string): Promise<void> {
        await this.ensureConnected();
        return await this.hubConnection.invoke<void>('LeaveContactImport', contactImportId);
    }

    private async ensureConnected(): Promise<void> {
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            await this.startConnection();
        }
    }
}
